import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";

interface ReportRow {
  report_id: string;
  template_id: string;
  status: string;
  requested_at: string;
  completed_at: string | null;
  file_size_bytes: number | null;
  cooperatie_id: string;
}

export default function RecentReportsTab() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const reports = useQuery({
    queryKey: ["reports"],
    queryFn: async () => (await api.get<ReportRow[]>("/reports")).data,
    refetchInterval: 5_000,
  });

  const deleteMut = useMutation({
    mutationFn: async (reportId: string) => {
      await api.delete(`/reports/${reportId}`);
      return reportId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });

  const bulkDeleteMut = useMutation({
    mutationFn: async (reportIds: string[]) => {
      await Promise.all(reportIds.map((id) => api.delete(`/reports/${id}`)));
      return reportIds;
    },
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });

  const handleDelete = (reportId: string) => {
    if (confirm("Rapport definitief verwijderen?")) {
      deleteMut.mutate(reportId);
    }
  };

  const toggleOne = (reportId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) next.delete(reportId);
      else next.add(reportId);
      return next;
    });
  };

  const visibleIds = reports.data?.map((r) => r.report_id) ?? [];
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleIds));
  };

  const handleBulkDelete = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (confirm(`${ids.length} rapport${ids.length === 1 ? "" : "en"} definitief verwijderen?`)) {
      bulkDeleteMut.mutate(ids);
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-kbs-primary">Recente rapporten</h2>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={bulkDeleteMut.isPending}
            className="rounded bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            Verwijder geselecteerde ({selected.size})
          </button>
        )}
      </div>
      {reports.isLoading && <p className="mt-3 text-sm text-slate-500">Laden…</p>}
      {reports.error && (
        <p className="mt-3 text-sm text-rose-600">Fout: {(reports.error as Error).message}</p>
      )}
      {reports.data && reports.data.length === 0 && (
        <p className="mt-3 text-sm text-slate-500">Nog geen rapporten gegenereerd.</p>
      )}
      {reports.data && reports.data.length > 0 && (
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-2 font-medium">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Selecteer alle rapporten"
                  className="h-4 w-4 cursor-pointer rounded border-slate-300"
                />
              </th>
              <th className="py-2 font-medium">Aangevraagd</th>
              <th className="py-2 font-medium">Template</th>
              <th className="py-2 font-medium">Status</th>
              <th className="py-2 font-medium">Grootte</th>
              <th className="py-2 font-medium">Actie</th>
            </tr>
          </thead>
          <tbody>
            {reports.data.map((r) => (
              <tr key={r.report_id} className="border-b border-slate-100">
                <td className="py-2 pr-2">
                  <input
                    type="checkbox"
                    checked={selected.has(r.report_id)}
                    onChange={() => toggleOne(r.report_id)}
                    aria-label={`Selecteer rapport ${r.report_id}`}
                    className="h-4 w-4 cursor-pointer rounded border-slate-300"
                  />
                </td>
                <td className="py-2">{new Date(r.requested_at).toLocaleString("nl-NL")}</td>
                <td className="py-2">{r.template_id}</td>
                <td className="py-2">
                  <StatusBadge status={r.status} />
                </td>
                <td className="py-2 text-slate-600">
                  {r.file_size_bytes ? `${Math.round(r.file_size_bytes / 1024)} KB` : "—"}
                </td>
                <td className="py-2">
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/rapport/${r.report_id}`}
                      className="text-kbs-accent hover:underline"
                    >
                      Bekijk →
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(r.report_id)}
                      disabled={deleteMut.isPending}
                      className="text-rose-600 hover:underline disabled:opacity-40"
                      title="Rapport verwijderen"
                    >
                      Verwijder
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    PENDING: "bg-slate-100 text-slate-700",
    RUNNING: "bg-amber-100 text-amber-800",
    COMPLETED: "bg-emerald-100 text-emerald-800",
    FAILED: "bg-rose-100 text-rose-800",
    CANCELLED: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${colors[status] ?? colors.PENDING}`}>
      {status}
    </span>
  );
}
